import { useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { DraftDocumentEditor } from '../components/DraftDocumentEditor.js';
import { formatMoney, toCents } from '@monrdv/shared';
import { request } from '../lib/api.js';
import { RecordLink } from '../components/RecordLink.js';
import { AttachmentSection } from '../components/Attachments.js';

/**
 * One quote.
 *
 * Same document layout as an invoice, with one thing an invoice does not have: **optional
 * lines**. A quote can offer upsells the customer takes or leaves, and 2736 historical quotes
 * used it. Showing an unselected optional line as though it were part of the price would
 * misstate the total; hiding it would lose the fact that it was offered and declined.
 *
 * So optional lines are always visible, marked, and excluded from the total when unselected —
 * with the offered-but-not-taken value stated separately, because "what did we fail to sell"
 * is worth knowing.
 */

type TaxLine = { code: string; rate: string; amount: string; registrationNumber: string | null };

type QuoteItem = {
  id: string;
  name: string;
  description: string | null;
  quantity: string;
  unitPrice: string;
  unitOfMeasure: string | null;
  optional: boolean;
  selected: boolean;
  subtotal: string;
  total: string;
};

type Quote = {
  id: string;
  number: string | null;
  status: string;
  issuedOn: string | null;
  expiresOn: string | null;
  acceptedAt: string | null;
  declinedAt: string | null;
  subtotal: string;
  taxTotal: string;
  total: string;
  taxes: TaxLine[];
  note: string | null;
  sentAt: string | null;
  firstViewedAt: string | null;
  billingSnapshot: { billedTo: string; organization: string | null } | null;
  customer: { id: string; name: string };
  job: { id: string | null; number: string } | null;
  items: QuoteItem[];
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'Brouillon',
  pending: 'En attente de réponse',
  accepted: 'Acceptée',
  declined: 'Refusée',
  expired: 'Expirée',
};

export function QuoteDetailScreen() {
  const { quoteId } = useParams({ from: '/quotes/$quoteId' });

  const query = useQuery({
    queryKey: ['quote', quoteId],
    queryFn: () => request<Quote>(`/quotes/${quoteId}`),
  });

  if (query.isLoading) return <p className="screen__loading">Chargement…</p>;
  if (query.isError || !query.data) {
    return (
      <div className="screen">
        <div className="notice notice--error">Cette soumission est introuvable.</div>
      </div>
    );
  }

  const quote = query.data;

  // What was offered and not taken. Counted from unselected optional lines, which is the only
  // place that information exists.
  const declinedValue = quote.items
    .filter((item) => item.optional && !item.selected)
    .reduce((sum, item) => sum + toCents(item.subtotal), 0);

  const expired = quote.status === 'pending' && quote.expiresOn && new Date(quote.expiresOn) < new Date();

  const decidedOn = quote.acceptedAt ?? quote.declinedAt;

  return (
    <div className="screen screen--customer">
      <RecordLink kind="customer" id={quote.customer.id} className="screen__breadcrumb">
        ← {quote.customer.name}
      </RecordLink>

      <header className="doc__head">
        <div className="doc__identity">
          <span className="label">Soumission</span>
          <h1 className="doc__number tnum">{quote.number ?? 'Sans numéro'}</h1>
          <span className={`chip doc__status doc__status--${quote.status}`}>
            {expired ? 'Échue' : STATUS_LABEL[quote.status] ?? quote.status}
          </span>
        </div>

        <address className="doc__billed">
          <span className="label">Pour</span>
          <span className="doc__billedName">{quote.billingSnapshot?.billedTo ?? quote.customer.name}</span>
          {quote.billingSnapshot?.organization && <span>{quote.billingSnapshot.organization}</span>}
        </address>

        <dl className="doc__meta">
          <div>
            <dt className="label">Émise</dt>
            <dd>{quote.issuedOn ? new Date(quote.issuedOn).toLocaleDateString('fr-CA') : '—'}</dd>
          </div>
          <div>
            <dt className="label">{expired ? 'Échue le' : 'Valide jusqu’au'}</dt>
            <dd className={expired ? 'doc__overdue' : undefined}>
              {quote.expiresOn ? new Date(quote.expiresOn).toLocaleDateString('fr-CA') : '—'}
            </dd>
          </div>
          {decidedOn && (
            <div>
              <dt className="label">{quote.acceptedAt ? 'Acceptée' : 'Refusée'}</dt>
              <dd>
                {new Date(decidedOn).toLocaleDateString('fr-CA')}
                {/* How long the customer took to decide, which is the useful part when you
                    are looking at a quote that has been sitting. */}
                {quote.issuedOn && (
                  <span className="doc__lag">
                    {' '}
                    · {Math.round((new Date(decidedOn).getTime() - new Date(quote.issuedOn).getTime()) / 86_400_000)} j
                  </span>
                )}
              </dd>
            </div>
          )}
          {quote.job && (
            <div>
              <dt className="label">Travail</dt>
              <dd>
                {quote.job.id ? (
                  <RecordLink kind="job" id={quote.job.id} className="cell__link">
                    {quote.job.number}
                  </RecordLink>
                ) : (
                  quote.job.number
                )}
              </dd>
            </div>
          )}
        </dl>
      </header>

      <table className="table doc__items">
        <caption>{quote.items.length} ligne(s)</caption>
        <thead>
          <tr>
            <th scope="col">Description</th>
            <th scope="col" style={{ textAlign: 'right' }}>Qté</th>
            <th scope="col" style={{ textAlign: 'right' }}>Prix</th>
            <th scope="col" style={{ textAlign: 'right' }}>Montant</th>
          </tr>
        </thead>
        <tbody>
          {quote.items.map((item) => {
            const excluded = item.optional && !item.selected;
            return (
              <tr key={item.id} className={excluded ? 'doc__itemRow--excluded' : undefined}>
                <td className="cell">
                  <span className="doc__itemName">{item.name}</span>
                  {item.optional && (
                    <span className={`chip ${item.selected ? '' : 'chip--quiet'} doc__optional`}>
                      {item.selected ? 'Option retenue' : 'Option non retenue'}
                    </span>
                  )}
                  {item.description && (
                    <details className="doc__itemDetails">
                      <summary>Détails</summary>
                      <p>{item.description}</p>
                    </details>
                  )}
                </td>
                <td className="cell numeric">
                  {item.quantity}
                  {item.unitOfMeasure ? ` ${item.unitOfMeasure}` : ''}
                </td>
                <td className="cell numeric money">{formatMoney(toCents(item.unitPrice), 'fr')}</td>
                <td className="cell numeric money">
                  {excluded ? (
                    <span className="money--settled">({formatMoney(toCents(item.subtotal), 'fr')})</span>
                  ) : (
                    formatMoney(toCents(item.subtotal), 'fr')
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <section className="doc__totals" aria-label="Totaux">
        <div className="doc__totalRow">
          <span>Sous-total</span>
          <span className="money">{formatMoney(toCents(quote.subtotal), 'fr')}</span>
        </div>
        {quote.taxes.map((tax) => (
          <div key={tax.code} className="doc__totalRow doc__totalRow--tax">
            <span>
              {tax.code} <span className="tnum">{tax.rate}%</span>
            </span>
            <span className="money">{formatMoney(toCents(tax.amount), 'fr')}</span>
          </div>
        ))}
        <div className="doc__totalRow doc__totalRow--grand">
          <span>Total</span>
          <span className="money">{formatMoney(toCents(quote.total), 'fr')}</span>
        </div>

        {declinedValue > 0 && (
          <div className="doc__totalRow doc__totalRow--margin">
            <span>Options non retenues</span>
            <span className="money money--settled">{formatMoney(declinedValue, 'fr')}</span>
          </div>
        )}
      </section>

      {quote.note && (
        <section className="doc__section">
          <h2 className="section__title">Note</h2>
          <p className="doc__note">{quote.note}</p>
        </section>
      )}

      {/* Priced lines can still be changed while this is a draft, and not afterwards. */}
      <section className="doc__section">
        <h2 className="section__title">Modifier le brouillon</h2>
        <DraftDocumentEditor
          kind="quote"
          id={quote.id}
          status={quote.status}
          note={quote.note}
          date={quote.expiresOn}
          items={quote.items}
          locale="fr"
        />
      </section>

      {/* The site photographs and measurements the price was built from — reached through
          the job, because that is where a file is filed. */}
      <AttachmentSection scope={{ quoteId }} />
    </div>
  );
}
